import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Store, ChevronDown, ChevronUp, Plus, Loader2, Trash2 } from "lucide-react";
import api from "@/lib/api";

/**
 * Manages every dealership in the caller's owner_group_id.
 * - Renders a full-profile editor per sibling store (name, address, phone,
 *   email, website, logo). Each store's card is collapsible so the section
 *   stays compact by default.
 * - Provides a "+ Criar nova loja" button that expands into a blank profile
 *   form so the owner can add a new store without leaving the settings page.
 * - The active store is excluded (it's edited via the section above).
 */
export default function OtherStoresSection({ activeDealershipId, onRefresh }) {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/dealerships/mine");
      setStores(r.data || []);
    } catch (e) {
      toast.error("Erro ao carregar as outras lojas");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const others = stores.filter((s) => s.id !== activeDealershipId);

  return (
    <div className="border border-border p-6 mb-6" data-testid="other-stores-section">
      <div className="flex items-center gap-2 mb-1">
        <Store size={16} className="text-brand" />
        <p className="label-eyebrow text-brand">Outras lojas do grupo</p>
      </div>
      <p className="text-text-secondary text-sm mb-6">
        Edite o perfil completo de qualquer outra loja do seu grupo — ou crie uma nova.
      </p>

      {loading && (
        <div className="flex items-center gap-2 text-text-secondary text-sm">
          <Loader2 size={14} className="animate-spin" /> Carregando...
        </div>
      )}

      {!loading && others.length === 0 && !creating && (
        <div className="rounded border border-dashed border-border p-6 text-center">
          <Store size={24} className="mx-auto mb-2 text-text-secondary" />
          <p className="text-sm text-text-secondary mb-4">
            Você tem apenas 1 loja no grupo. Adicione a próxima abaixo.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {others.map((s) => (
          <StoreCard
            key={s.id}
            store={s}
            expanded={expandedId === s.id}
            onToggle={() => setExpandedId(expandedId === s.id ? null : s.id)}
            onSaved={() => { load(); onRefresh && onRefresh(); }}
            onDeleted={() => { setExpandedId(null); load(); onRefresh && onRefresh(); }}
          />
        ))}
      </div>

      {creating ? (
        <div className="mt-4 rounded border border-brand p-4">
          <p className="label-eyebrow text-brand mb-3">Nova loja no grupo</p>
          <StoreProfileForm
            initial={{ name: "", address: "", phone: "", email: "", website: "", logo_url: "" }}
            submitLabel="Criar loja"
            onSubmit={async (payload) => {
              const r = await api.post("/dealerships/create", payload);
              toast.success(`Loja "${r.data.name}" criada`);
              setCreating(false);
              load();
              onRefresh && onRefresh();
            }}
            onCancel={() => setCreating(false)}
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setCreating(true)}
          data-testid="btn-new-store"
          className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 border border-dashed border-border hover:border-brand hover:text-brand text-text-secondary text-sm font-display font-bold uppercase tracking-widest transition-colors"
        >
          <Plus size={14} /> Criar nova loja
        </button>
      )}
    </div>
  );
}

function StoreCard({ store, expanded, onToggle, onSaved, onDeleted }) {
  return (
    <div className="border border-border rounded overflow-hidden" data-testid={`store-card-${store.id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-bg-elev hover:bg-brand/5 transition-colors text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          {store.logo_url ? (
            <img src={store.logo_url} alt={store.name} className="w-8 h-8 object-contain rounded" />
          ) : (
            <div className="w-8 h-8 bg-surface border border-border rounded flex items-center justify-center">
              <Store size={14} className="text-text-secondary" />
            </div>
          )}
          <div className="min-w-0">
            <p className="font-display font-bold text-sm truncate">{store.name}</p>
            {store.address && <p className="text-[11px] text-text-secondary truncate">{store.address}</p>}
          </div>
        </div>
        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {expanded && (
        <div className="p-4 border-t border-border">
          <StoreProfileForm
            initial={store}
            submitLabel="Salvar loja"
            onSubmit={async (payload) => {
              await api.put(`/dealerships/${store.id}`, payload);
              toast.success("Loja atualizada");
              onSaved && onSaved();
            }}
            onDelete={async () => {
              if (!window.confirm(`Excluir a loja "${store.name}" DEFINITIVAMENTE? Esta ação não pode ser desfeita.`)) return;
              try {
                await api.delete(`/dealerships/${store.id}`);
                toast.success("Loja excluída");
                onDeleted && onDeleted();
              } catch (e) {
                toast.error(e.response?.data?.detail || "Erro ao excluir");
              }
            }}
          />
        </div>
      )}
    </div>
  );
}

function StoreProfileForm({ initial, submitLabel, onSubmit, onCancel, onDelete }) {
  const [form, setForm] = useState({
    name: initial.name || "",
    short_name: initial.short_name || "",
    logo_url: initial.logo_url || "",
    address: initial.address || "",
    phone: initial.phone || "",
    email: initial.email || "",
    website: initial.website || "",
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const onLogoPick = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      // Signed upload via our Cloudinary helper endpoint.
      const sig = await api.get("/cloudinary/signature", { params: { folder: "dealership-logos" } });
      const { signature, timestamp, api_key, cloud_name, folder } = sig.data;
      const fd = new FormData();
      fd.append("file", file);
      fd.append("api_key", api_key);
      fd.append("timestamp", timestamp);
      fd.append("signature", signature);
      fd.append("folder", folder);
      const cloudRes = await fetch(`https://api.cloudinary.com/v1_1/${cloud_name}/image/upload`, { method: "POST", body: fd });
      const cloudData = await cloudRes.json();
      if (!cloudData.secure_url) throw new Error("Upload failed");
      set("logo_url", cloudData.secure_url);
      toast.success("Logo carregada");
    } catch (e) {
      toast.error("Erro ao subir logo");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e && e.preventDefault();
    if (!form.name.trim()) { toast.error("Nome da loja é obrigatório"); return; }
    setSaving(true);
    try {
      await onSubmit(form);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      {/* Logo uploader */}
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 bg-surface border border-border rounded flex items-center justify-center overflow-hidden">
          {form.logo_url ? <img src={form.logo_url} alt="logo" className="w-full h-full object-contain" /> : <Store size={20} className="text-text-secondary" />}
        </div>
        <label className={`text-xs px-3 py-2 border border-border cursor-pointer hover:bg-bg-elev inline-flex items-center gap-1 ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
          {uploading && <Loader2 size={12} className="animate-spin" />} {form.logo_url ? "Trocar logo" : "Enviar logo"}
          <input type="file" accept="image/*" hidden onChange={(e) => onLogoPick(e.target.files?.[0])} />
        </label>
        {form.logo_url && (
          <button type="button" onClick={() => set("logo_url", "")} className="text-xs text-text-secondary hover:text-brand underline">
            remover
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="label-eyebrow block mb-1">Nome da loja *</label>
          <input
            data-testid="store-form-name"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="Ex: Intercar Peabody"
            className="w-full bg-surface border border-border px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Telefone</label>
          <input
            data-testid="store-form-phone"
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
            placeholder="(978) 555-0101"
            className="w-full bg-surface border border-border px-3 py-2 text-sm"
          />
        </div>
      </div>
      <div>
        <label className="label-eyebrow block mb-1">Etiqueta (nome curto)</label>
        <input
          data-testid="store-form-short-name"
          value={form.short_name}
          onChange={(e) => set("short_name", e.target.value)}
          placeholder='Ex: "Peabody"'
          className="w-full bg-surface border border-border px-3 py-2 text-sm"
        />
        <p className="text-[10px] text-text-secondary mt-1">
          Aparece nas faixas dos carros, no ranking e no botão de troca de loja. Vazio = usa o nome completo.
        </p>
      </div>
      <div>
        <label className="label-eyebrow block mb-1">Endereço completo</label>
        <input
          data-testid="store-form-address"
          value={form.address}
          onChange={(e) => set("address", e.target.value)}
          placeholder="98 Foster St, Peabody, MA 01960"
          className="w-full bg-surface border border-border px-3 py-2 text-sm"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="label-eyebrow block mb-1">Email</label>
          <input
            data-testid="store-form-email"
            type="email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            placeholder="peabody@intercar.com"
            className="w-full bg-surface border border-border px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Website</label>
          <input
            data-testid="store-form-website"
            value={form.website}
            onChange={(e) => set("website", e.target.value)}
            placeholder="intercarpeabody.com"
            className="w-full bg-surface border border-border px-3 py-2 text-sm"
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
        {onDelete && (
          <button
            type="button"
            onClick={onDelete}
            data-testid="btn-delete-store"
            className="inline-flex items-center gap-1 text-xs text-danger hover:opacity-80"
          >
            <Trash2 size={12} /> Excluir loja
          </button>
        )}
        <div className="flex gap-2 ml-auto">
          {onCancel && (
            <button type="button" onClick={onCancel} className="px-4 py-2 text-xs border border-border rounded hover:bg-bg-elev font-display font-bold uppercase tracking-widest">
              Cancelar
            </button>
          )}
          <button
            type="submit"
            disabled={saving}
            data-testid="btn-save-store-profile"
            className="px-4 py-2 text-xs bg-brand text-white rounded font-display font-bold uppercase tracking-widest hover:bg-brand/90 disabled:opacity-50"
          >
            {saving ? "Salvando..." : submitLabel}
          </button>
        </div>
      </div>
    </form>
  );
}
